'use strict'
/**
 * quinceObligatorias.js · EL ALTA, EN QUINCE RESPUESTAS.
 * ══════════════════════════════════════════════════════
 * SALTO:onboarding-10 · T3b · orden del founder (2026-09-25): «recorte del alta a 15 obligatorias:
 * fuera nombre (opcional al final), ciudad (del huso), evolución del peso y "cómo te ves" (raíles día
 * 3), "estado actual" plegado en años; el gancho dice "unos minutos" hasta que el alta real mida menos
 * de 3».
 *
 * 🔴 LO QUE SE PREGUNTABA: **veinte** respuestas obligatorias en cuatro pantallas (2 · 4 · 9 · 5,
 * cronometradas en el simulador contra prod el 2026-09-25). Y cinco de ellas no cambiaban el primer
 * plan:
 *   · **el nombre** — el motor no decide nada con él. Se pide al final, opcional, cuando ya hay plan;
 *   · **la ciudad** — el huso del dispositivo la dice, y el huso es lo que el motor usa de verdad
 *     (`todayFor.timezoneOf` lee `device_timezone` primero). Preguntar lo que ya se sabe es cobrar dos veces;
 *   · **la evolución del peso** y **«cómo te ves»** — son dos de las ocho señales del fenotipo, y el
 *     fenotipo las pondera: sin ellas clasifica igual, sólo con menos precisión. Se preguntan el día 3,
 *     cuando ya hay plan y la respuesta se nota;
 *   · **«estado actual»** — no es otra pregunta: es la misma. «Tres años y sigo» y «tres años y estoy
 *     parado» son dos respuestas de la MISMA pregunta, y aquí van juntas.
 *
 * LO QUE NO SE TOCA: las quince que quedan cambian el primer plan. Este fichero es la lista, y su guard
 * la compara contra lo que las pantallas exigen de verdad — una lista que no se compara es un comentario.
 */

/**
 * LAS QUINCE. Cada una con la pantalla donde vive y qué decide: una pregunta obligatoria que no cambie
 * el primer plan no es obligatoria, es una costumbre.
 */
const OBLIGATORIAS = Object.freeze([
  { key: 'primary_goal', pantalla: 'objetivo', decide: 'la familia del plan, sus pesos y el arco entero' },
  { key: 'goal_subtype', pantalla: 'objetivo', decide: 'la distancia o el detalle dentro de la familia; condicional, sólo si la familia lo pide' },
  { key: 'training_years_bucket', pantalla: 'deporte', decide: 'el nivel de partida y, con él, el volumen y la progresión' },
  { key: 'training_environment', pantalla: 'deporte', decide: 'qué ejercicios existen: no se prescribe una polea a quien entrena en casa' },
  { key: 'age', pantalla: 'punto_de_partida', decide: 'el metabolismo basal y los perímetros de seguridad' },
  { key: 'sex_at_birth', pantalla: 'punto_de_partida', decide: 'el metabolismo basal, el ciclo y los rangos de referencia' },
  { key: 'gdpr_consent', pantalla: 'punto_de_partida', decide: 'que podamos tratar datos de salud; sin esto no hay app' },
  { key: 'height_cm', pantalla: 'punto_de_partida', decide: 'el metabolismo basal y, con el peso, el punto de partida del cuerpo' },
  { key: 'weight_kg', pantalla: 'punto_de_partida', decide: 'el metabolismo basal, las cargas y el gasto de cada sesión' },
  { key: 'wake_time', pantalla: 'vida', decide: 'la ventana de sueño y el reparto de comidas del día' },
  { key: 'sleep_time', pantalla: 'vida', decide: 'la ventana de sueño y la hora de la última toma' },
  { key: 'training_days_available', pantalla: 'vida', decide: 'cuántas sesiones caben en la semana' },
  { key: 'training_window_pref', pantalla: 'vida', decide: 'a qué hora se coloca la sesión, y con ella las comidas' },
  { key: 'nutrition_style', pantalla: 'vida', decide: 'qué alimentos entran en el plato' },
  { key: 'sleep_window', pantalla: 'vida', decide: 'coherencia: no es una pregunta más, es que las dos horas se sostengan' },
])

/**
 * COLA:la-traza-del-alta-cubre-toda-clave-servida · EL MAPA CLAVE → COLUMNA, DECLARADO.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Orden del founder (2026-10-18): «TRAZA DEL ALTA: mapa clave→columna declarado en
 * quinceObligatorias.js (sex_at_birth → gender, etc.)».
 *
 * 🔴 DE DÓNDE VIENE. La TRAZA contaba por el nombre de la CLAVE, y la clave no es el nombre de la
 * columna. Comprobado contra el catálogo de PRODUCCIÓN el 2026-10-18, una a una: **cuatro de las
 * quince no se leen tal cual**, así que la tabla de 75 celdas contaba cuatro ceros por persona que
 * no eran ceros — 20 de 75 celdas mal, un 27 % del instrumento.
 *
 * `null` significa **no hay columna**: no es un olvido, es que esa obligatoria no se guarda en una
 * columna propia y se comprueba de otra manera (y se dice cómo).
 *
 * COLA:el-alta-no-persiste-hasta-el-final · y este mapa es además **por dónde escribe el alta**: `escritoraDelAlta.js` deriva de aquí las catorce
 * columnas que cada pantalla guarda al avanzar. Un segundo diccionario para las mismas quince preguntas es cómo se acaba escribiendo en una columna
 * que no existe.
 */
const COLUMNA_DE = Object.freeze({
  // Las once que se leen tal cual (comprobado: existen en `profiles` en producción).
  primary_goal:            'profiles.primary_goal',
  goal_subtype:            'profiles.goal_subtype',
  training_years_bucket:   'profiles.training_years_bucket',
  training_environment:    'profiles.training_environment',
  age:                     'profiles.age',
  height_cm:               'profiles.height_cm',
  weight_kg:               'profiles.weight_kg',
  wake_time:               'profiles.wake_time',
  sleep_time:              'profiles.sleep_time',
  training_window_pref:    'profiles.training_window_pref',
  nutrition_style:         'profiles.nutrition_style',
  // Las tres renombradas. `sex_at_birth` NO existe como columna, ni en prod ni en el libro.
  sex_at_birth:            'profiles.gender',
  gdpr_consent:            'profiles.gdpr_health_data_consent',
  training_days_available: 'profiles.days_per_week',
  // Y la que no es columna: es coherencia entre dos horas que sí lo son.
  sleep_window:            null,
})

/**
 * LA CONDICIÓN DE `goal_subtype` · es un hecho de los objetivos, así que vive con ellos (`engine/goal/catalogoDeObjetivos.js`) y aquí sólo se
 * re-exporta, para que quien lea la lista de las quince tenga a mano cómo se cuenta la condicional sin copiarse una segunda lista de familias.
 */
const { FAMILIAS_SIN_SUBTIPO, subtipoDelObjetivoCubierto } = require('../../engine/goal/catalogoDeObjetivos')

/** Por qué `sleep_window` no tiene columna, dicho donde se lee y no en un comentario de paso. */
const SIN_COLUMNA = Object.freeze({
  sleep_window: 'no es un dato, es que `wake_time` y `sleep_time` se sostengan juntas. Se comprueba con las dos, no con una columna propia.',
})

/** La columna donde cae una obligatoria, o `null` si no hay. Lanza con una clave que no es del alta. */
function columnaDe(clave) {
  if (!(clave in COLUMNA_DE)) throw new Error(`columnaDe: «${clave}» no es una obligatoria del alta (${Object.keys(COLUMNA_DE).join(', ')})`)
  return COLUMNA_DE[clave]
}

/** Las que salen, con a dónde van. Ninguna desaparece: cambia cuándo se pregunta. */
const FUERA = Object.freeze({
  name: { a: 'opcional, al final', porque: 'el motor no decide nada con el nombre. Se pide cuando ya hay plan que enseñar, y se puede no contestar.' },
  city: { a: 'derivada del huso', porque: 'el huso del dispositivo ya la dice, y es el huso —no la ciudad— lo que el motor usa para saber qué día es para ti.' },
  weight_trend: { a: 'raíl del día 3', porque: 'es una de las ocho señales del fenotipo, que las PONDERA: sin ella clasifica igual, con menos precisión. Y el día 3 la respuesta se nota, porque ya hay plan que ajustar.' },
  body_composition_self: { a: 'raíl del día 3', porque: 'la señal 6 del fenotipo, la misma historia. Además es la pregunta más incómoda del alta, y preguntarla en el minuto dos a alguien que aún no tiene nada es cobrar antes de servir.' },
  training_active_status: { a: 'plegada en los años', porque: 'no es otra pregunta: «tres años y sigo» y «tres años y estoy parado» son dos respuestas de la misma. Se responden juntas y el escritor sigue escribiendo las dos columnas.' },
})

/** El gancho promete lo que el alta mide. Mientras el alta real no baje de 3 minutos, dice «unos minutos». */
const MINUTOS_MEDIDOS = 7.5          // simulador iPhone 17 Pro contra prod, 2026-09-25: 7 min 32 s
const PROMESA_CORTA = 'en menos de 3 minutos'
const PROMESA_HONESTA = 'en unos minutos'
const promesaDelGancho = (minutos = MINUTOS_MEDIDOS) => (Number(minutos) < 3 ? PROMESA_CORTA : PROMESA_HONESTA)

module.exports = { OBLIGATORIAS, COLUMNA_DE, SIN_COLUMNA, columnaDe, FAMILIAS_SIN_SUBTIPO, subtipoDelObjetivoCubierto, FUERA, MINUTOS_MEDIDOS, PROMESA_CORTA, PROMESA_HONESTA, promesaDelGancho }
